import { EntitiesChanges, LogDisplay } from './log'

// A prepared org-chart change inside a proposal (not yet applied to the DB).
// Stored in the proposal activity data and replayed onto the org on approval.
export interface ProposalLog {
  id: string
  display: LogDisplay
  changes: EntitiesChanges
  // Set when replaying this log fails (e.g. a referenced entity no longer
  // exists). The proposal is invalid until the faulty log is removed.
  error?: string
}

// Mirrors the org column default, used when the setting has not loaded yet.
export const defaultProposalDurationDays = 10

export type ProposalDecisionMode =
  // Decided in a meeting: no vote is ever counted, a human records the outcome.
  // Carries no resolutionDate, so the cron never picks it up (D6, 06/08/2026).
  | 'meeting'
  | 'consent' // approved if no objection
  | 'unanimity' // approved if everyone approves
  | 'simpleMajority' // approve > object (votes cast)
  | 'absoluteMajority' // approve > half of participants

export type ProposalStatus =
  | 'inProgress'
  | 'approved'
  | 'refused'
  | 'canceled'

// Who can vote on the proposal. 'circle' = the circle's participants (shown as
// "role" in the UI, but the data model attaches participants to a circle).
export type ProposalVotersScope = 'thread' | 'circle'

export type ProposalVoteValue = 'approve' | 'object' | 'abstain'

export interface ThreadActivityDataProposal {
  title: string
  description: string // rich text (markdown)
  decisionMode: ProposalDecisionMode
  votersScope: ProposalVotersScope
  allowAbstain: boolean // offer an abstain vote (ignored when decisionMode is 'consent')
  showVoters: boolean // reveal who voted what at resolution
  // Auto-resolve as soon as the outcome is certain (before everyone has voted)
  earlyResolution: boolean
  resolutionDate: string | null // ISO deadline, auto-resolved when reached
  logs: ProposalLog[] // prepared org-chart changes
  status: ProposalStatus
  resolvedAt: string | null
  appliedDecisionId: string | null // decision created in the circle registry on approval
  // The policy this proposal is about, when it edits an existing one instead of
  // deciding something new (GOUV-14, 08/08/2026). Approving a proposal creates
  // a registry entry unconditionally today; with a target it updates THAT
  // policy and creates none, so editing a policy does not leave a duplicate
  // beside it. The proposal's own title and description become the policy's,
  // exactly as they already do when a policy is created.
  //
  // Optional, and it must stay so: every proposal already in the database was
  // written without these fields and has to keep reading.
  targetDecisionId?: string | null
  // The target is to be archived rather than rewritten. The proposal's text
  // then argues for the removal and does not touch the policy's own.
  archiveTargetDecision?: boolean
  // The circle that decides this proposal, once settled (Yocoop, 09/08/2026).
  //
  // Only ever set for a tension carried by a LINK or a CIRCLE (#121), the
  // cases with two possible deciding circles (the one a link sits in and the
  // one it represents; a circle itself and the circle above). It is
  // fixed by the first change the author makes, not by a question put to them:
  // letting the author designate the circle would let them pick the friendlier
  // room. A tension on a plain role has a single possible circle, so this
  // stays empty and `getProposalVoterCircleId` answers alone.
  //
  // Optional, and it must stay so: every proposal already in the database was
  // written without this field and has to keep reading.
  decidingCircleId?: string | null
  // Why an approved proposal could not be applied — typically its target policy
  // was archived or deleted while the vote ran. Falling back to creating a new
  // policy would resurrect, as a duplicate, something the circle deliberately
  // removed; so nothing is applied and the reason is shown instead.
  applyError?: string | null
  // The opening email has been CLAIMED (E3, 13/08/2026) — which is not the same
  // as "emails went out". It is set before the first send, by a conditional
  // write that only one caller can win, and it stays set even when nobody
  // wanted the email. Reading it as "someone was notified" would make an
  // unwanted proposal eligible again at every replay.
  //
  // It lives here rather than in a column of its own because
  // `thread_activity.data` is a plain jsonb, absent from
  // `model/schema-overrides.gql`: adding a field costs no migration and no
  // codegen. The claim stays atomic all the same — Hasura can guard a jsonb
  // update with `_not: { _contains: { openedNotified: true } }` and hand back
  // `affected_rows`.
  //
  // Optional, and it must stay so: every proposal already in the database was
  // written without this field and has to keep reading.
  openedNotified?: boolean
}

export interface ProposalVoteResult {
  userId: string
  vote: ProposalVoteValue
  // Voter member id, recorded at resolution time. Stable across archiving
  // (unlike userId, which is nulled), so the voters modal can load the member
  // name/picture by id even after the member is archived. Optional for
  // snapshots written before this field existed (and not recoverable).
  memberId?: string
}

// Generic event appended to the thread about a proposal (resolution,
// cancellation, vote reminder, ...).
export type ProposalEventType = 'resolution' | 'canceled' | 'voteReminder'

export interface ThreadActivityDataProposalEvent {
  proposalActivityId: string
  event: ProposalEventType
  // Resolution-specific (only when event === 'resolution')
  status?: 'approved' | 'refused'
  decisionMode?: ProposalDecisionMode
  votes?: ProposalVoteResult[]
  showVoters?: boolean // reveal who voted what (else show counts only)
  decisionId?: string | null
  // Who clicked "Resolve now" to close the vote early, outside mode 'meeting'
  // (GOUV-06, 09/08/2026). Absent for the cron and for the automatic
  // resolution on a completed vote, which have no one to name; also absent for
  // 'meeting', where D6 already records its own outcome and this must not
  // shift that rendering by accident. The name travels alongside the id
  // because MemberLink needs one and this event carries none otherwise —
  // following the same precedent as `log.memberName`, so the trace still
  // names the resolver even after their membership is archived.
  //
  // Optional, and it must stay so: every resolution event already in the
  // database was written without these fields and has to keep reading.
  resolvedByMemberId?: string
  resolvedByMemberName?: string
}
